import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookOpen, Plus, Edit, Trash2, Upload, Search, FileText, Sparkles, Target, CheckCircle, AlertCircle, Database, TrendingUp, Globe, Book } from 'lucide-react';
import { bookService } from '../../services/bookService';
import { aiService } from '../../services/aiService';
import { authService } from '../../services/authService';
import { bulkBooksService } from '../../services/bulkBooksService';
import { openLibraryService } from '../../services/openLibraryService';
import { useToast } from '../../hooks/use-toast';
import BookPagination from '../../components/BookPagination';

const AdminBooks = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const ADMIN_PAGE_SIZE = 12;
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showOpenLibraryImport, setShowOpenLibraryImport] = useState(false);
  const [openLibraryData, setOpenLibraryData] = useState({
    importType: 'popular',
    subject: 'fiction',
    limit: 50
  });
  const [openLibraryImporting, setOpenLibraryImporting] = useState(false);
  const [openLibraryStats, setOpenLibraryStats] = useState(null);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [bulkImportData, setBulkImportData] = useState({
    limit: 25,
    genreFilter: 'all'
  });
  const [importStats, setImportStats] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [showAIQuizModal, setShowAIQuizModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [aiQuizGenerating, setAiQuizGenerating] = useState(false);
  const [aiConnectionTest, setAiConnectionTest] = useState(null);
  const [aiQuizData, setAiQuizData] = useState({
    quiz_title: '',
    difficulty: 'Medium',
    num_questions: 10,
    question_prompt: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    genre: '',
    description: '',
    published_year: '',
    cover_url: ''
  });

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  // Debounce search — reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch whenever page or debounced search changes
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { skip: (currentPage - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE };
        if (debouncedSearch) params.search = debouncedSearch;
        const countParams = debouncedSearch ? { search: debouncedSearch } : {};
        const [booksData, { count }] = await Promise.all([
          bookService.getBooks(params),
          bookService.getBookCount(countParams),
        ]);
        if (!cancelled) {
          setBooks(booksData);
          setTotalBooks(count);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching books:', error);
          toast({ title: "Error", description: "Failed to load books.", variant: "destructive" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = { skip: (currentPage - 1) * ADMIN_PAGE_SIZE, limit: ADMIN_PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;

      const countParams = debouncedSearch ? { search: debouncedSearch } : {};

      const [booksData, { count }] = await Promise.all([
        bookService.getBooks(params),
        bookService.getBookCount(countParams),
      ]);
      setBooks(booksData);
      setTotalBooks(count);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast({ title: "Error", description: "Failed to load books.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const bookData = {
        ...formData,
        published_year: parseInt(formData.published_year)
      };

      if (editingBook) {
        await bookService.updateBook(editingBook.id, bookData);
        toast({
          title: "Success",
          description: "Book updated successfully!",
        });
      } else {
        await bookService.createBook(bookData);
        toast({
          title: "Success",
          description: "Book created successfully!",
        });
      }

      resetForm();
      fetchBooks();
      
    } catch (error) {
      console.error('Error saving book:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save book.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (book) => {
    setFormData({
      title: book.title,
      author: book.author,
      genre: book.genre,
      description: book.description,
      published_year: book.published_year?.toString() || '',
      cover_url: book.cover_url || ''
    });
    setEditingBook(book);
    setShowAddForm(true);
  };

  const handleDelete = async (book) => {
    if (!window.confirm(`Are you sure you want to delete "${book.title}"? This will also delete all associated quizzes and questions.`)) {
      return;
    }

    try {
      await bookService.deleteBook(book.id);
      toast({
        title: "Success",
        description: "Book deleted successfully!",
      });
      setCurrentPage(1);
      fetchBooks();
    } catch (error) {
      console.error('Error deleting book:', error);
      toast({
        title: "Error",
        description: "Failed to delete book.",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      genre: '',
      description: '',
      published_year: '',
      cover_url: ''
    });
    setShowAddForm(false);
    setEditingBook(null);
  };

  const testAIConnection = async () => {
    try {
      const result = await aiService.testConnection();
      setAiConnectionTest(result);
      return result.success;
    } catch (error) {
      setAiConnectionTest({
        success: false,
        message: "Failed to connect to AI service"
      });
      return false;
    }
  };

  const handleAIQuizGenerate = async () => {
    if (!selectedBook || !aiQuizData.quiz_title.trim()) {
      toast({
        title: "Error",
        description: "Please enter a quiz title.",
        variant: "destructive",
      });
      return;
    }

    setAiQuizGenerating(true);
    try {
      const generateData = {
        book_title: selectedBook.title,
        book_author: selectedBook.author,
        quiz_title: aiQuizData.quiz_title,
        difficulty: aiQuizData.difficulty,
        num_questions: aiQuizData.num_questions,
        question_prompt: aiQuizData.question_prompt || undefined
      };

      const result = await aiService.generateQuiz(generateData);
      
      if (result.success) {
        toast({
          title: "Success",
          description: `Generated quiz "${aiQuizData.quiz_title}" with ${result.questions_created} questions!`,
        });
        
        // Reset form and close modal
        setShowAIQuizModal(false);
        setSelectedBook(null);
        setAiQuizData({
          quiz_title: '',
          difficulty: 'Medium',
          num_questions: 10,
          question_prompt: ''
        });
        setAiConnectionTest(null);
        
        // Refresh books data
        fetchBooks();
      } else {
        throw new Error(result.message || "Failed to generate quiz");
      }
      
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to generate quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAiQuizGenerating(false);
    }
  };

  const handleBulkImport = async () => {
    setBulkImporting(true);
    try {
      const result = await bulkBooksService.importPopularBooks(
        bulkImportData.limit,
        bulkImportData.genreFilter === 'all' ? null : bulkImportData.genreFilter
      );
      
      toast({
        title: "Success",
        description: result.message,
      });
      
      setShowBulkImport(false);
      setBulkImportData({ limit: 25, genreFilter: 'all' });
      setImportStats(null);
      fetchBooks(); // Refresh the books list
      
    } catch (error) {
      console.error('Error importing books:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to import books. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBulkImporting(false);
    }
  };

  const openBulkImportModal = async () => {
    setShowBulkImport(true);
    try {
      const stats = await bulkBooksService.getImportStats();
      setImportStats(stats);
    } catch (error) {
      console.error('Error fetching import stats:', error);
      toast({
        title: "Error",
        description: "Failed to load import statistics.",
        variant: "destructive",
      });
    }
  };

  const openOpenLibraryModal = async () => {
    setShowOpenLibraryImport(true);
    try {
      const [stats, subjects] = await Promise.all([
        openLibraryService.getStats(),
        openLibraryService.getAvailableSubjects()
      ]);
      setOpenLibraryStats(stats);
      setAvailableSubjects(subjects.subjects || []);
    } catch (error) {
      console.error('Error fetching Open Library data:', error);
      toast({
        title: "Error",
        description: "Failed to load Open Library information.",
        variant: "destructive",
      });
    }
  };

  const handleOpenLibraryImport = async () => {
    setOpenLibraryImporting(true);
    try {
      let result;
      
      if (openLibraryData.importType === 'popular') {
        result = await openLibraryService.importPopularBooks(openLibraryData.limit);
      } else if (openLibraryData.importType === 'classics') {
        result = await openLibraryService.importClassicLiterature(openLibraryData.limit);
      } else /* istanbul ignore else -- the import-type select only offers these three values */ if (openLibraryData.importType === 'subject') {
        result = await openLibraryService.importBooksBySubject(openLibraryData.subject, openLibraryData.limit);
      }
      
      toast({
        title: "Success",
        description: result.message,
      });
      
      setShowOpenLibraryImport(false);
      setOpenLibraryData({ importType: 'popular', subject: 'fiction', limit: 50 });
      setOpenLibraryStats(null);
      fetchBooks(); // Refresh the books list
      
    } catch (error) {
      console.error('Error importing from Open Library:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to import books from Open Library. Please try again.",
        variant: "destructive",
      });
    } finally {
      setOpenLibraryImporting(false);
    }
  };

  const openAIQuizModal = async (book) => {
    setSelectedBook(book);
    setAiQuizData({
      quiz_title: `${book.title} Quiz`,
      difficulty: 'Medium',
      num_questions: 10,
      question_prompt: ''
    });
    setShowAIQuizModal(true);
    await testAIConnection();
  };

  const handleCSVUpload = async () => {
    /* istanbul ignore next -- defensive guard; the import button is disabled without a file */
    if (!csvFile) {
      toast({
        title: "Error",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await bookService.importBooksCSV(csvFile);
      toast({
        title: "Success",
        description: `Books imported successfully! ${result.imported_count || 'Some'} books added.`,
      });
      setShowCSVImport(false);
      setCsvFile(null);
      fetchBooks();
    } catch (error) {
      console.error('Error importing CSV:', error);
      let errorMessage = "Failed to import CSV.";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.errors) {
        errorMessage = `Import errors: ${error.response.data.errors.slice(0, 3).join(', ')}`;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = [
      'title,author,genre,description,published_year,cover_url',
      '"To Kill a Mockingbird","Harper Lee","Fiction","A classic novel about...","1960","https://example.com/cover.jpg"',
      '"1984","George Orwell","Science Fiction","A dystopian novel...","1949","https://example.com/cover2.jpg"',
      '"Pride and Prejudice","Jane Austen","Romance","A classic romance...","1813","https://example.com/cover3.jpg"'
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'books_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "CSV template downloaded successfully!",
    });
  };

  return (
    <Layout isAdmin={true}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Books Management</h1>
                <p className="text-gray-600">Manage your book collection and create quizzes</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={openOpenLibraryModal}
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Import from Open Library
                </Button>
                <Button
                  onClick={openBulkImportModal}
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Database className="h-4 w-4 mr-2" />
                  Import Popular Books
                </Button>
                <Button
                  onClick={() => setShowCSVImport(true)}
                  variant="outline"
                  className="border-green-200 text-green-700 hover:bg-green-50"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Book
                </Button>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search books..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* AI Quiz Generation Modal */}
          {showAIQuizModal && selectedBook && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                  Create AI Quiz for "{selectedBook.title}"
                </CardTitle>
                
                {/* AI Connection Status */}
                {aiConnectionTest && (
                  <div className={`flex items-center space-x-2 text-sm ${
                    aiConnectionTest.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {aiConnectionTest.success ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    <span>{aiConnectionTest.message}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">Quick AI Quiz Creation</h4>
                  <p className="text-sm text-purple-800 mb-2">
                    Generate an intelligent quiz for <strong>"{selectedBook.title}"</strong> by {selectedBook.author}
                  </p>
                  <p className="text-xs text-purple-700">
                    Specify the number of questions, difficulty, and focus areas for AI generation.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="quiz_title">Quiz Title *</Label>
                    <Input
                      id="quiz_title"
                      value={aiQuizData.quiz_title}
                      onChange={(e) => setAiQuizData({...aiQuizData, quiz_title: e.target.value})}
                      placeholder="e.g., Pride and Prejudice Complete Quiz"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="difficulty">Difficulty Level</Label>
                    <Select value={aiQuizData.difficulty} onValueChange={(value) => setAiQuizData({...aiQuizData, difficulty: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Easy">Easy</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="Hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="num_questions">Number of Questions</Label>
                    <Select value={aiQuizData.num_questions.toString()} onValueChange={(value) => setAiQuizData({...aiQuizData, num_questions: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 questions</SelectItem>
                        <SelectItem value="8">8 questions</SelectItem>
                        <SelectItem value="10">10 questions</SelectItem>
                        <SelectItem value="15">15 questions</SelectItem>
                        <SelectItem value="20">20 questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="question_prompt">Focus Areas (Optional)</Label>
                  <Textarea
                    id="question_prompt"
                    value={aiQuizData.question_prompt}
                    onChange={(e) => setAiQuizData({...aiQuizData, question_prompt: e.target.value})}
                    placeholder="e.g., Focus on character relationships, major themes, plot developments..."
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAIQuizGenerate} 
                    disabled={aiQuizGenerating || (aiConnectionTest && !aiConnectionTest.success)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    {aiQuizGenerating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    {aiQuizGenerating ? 'Generating Quiz...' : 'Create Quiz with AI'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowAIQuizModal(false);
                      setSelectedBook(null);
                      setAiConnectionTest(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                
                {aiConnectionTest && aiConnectionTest.success && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>✓ Connected to Gemini AI</strong><br />
                      Ready to generate intelligent questions for "{selectedBook.title}".
                    </p>
                  </div>
                )}
                
                {aiConnectionTest && !aiConnectionTest.success && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      <strong>⚠ AI Service Issue:</strong><br />
                      {aiConnectionTest.message}. Please check your configuration.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* CSV Import Modal */}
          {showCSVImport && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle>Import Books from CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">CSV Format:</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Your CSV should include: title, author, genre, description, published_year, cover_url
                  </p>
                  <Button
                    onClick={downloadCSVTemplate}
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    Download Template
                  </Button>
                </div>
                
                <div>
                  <Label htmlFor="csvFile">Select CSV File</Label>
                  <Input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={handleCSVUpload} disabled={!csvFile}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Books
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowCSVImport(false);
                      setCsvFile(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bulk Import Popular Books Modal */}
          {showBulkImport && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="h-5 w-5 mr-2 text-purple-600" />
                  Import Popular Books Collection
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Popular Books Collection
                  </h4>
                  <p className="text-sm text-purple-800 mb-2">
                    Import from our curated collection of classic and popular books including works by Shakespeare, 
                    Dickens, Jane Austen, J.K. Rowling, George Orwell, and many more literary classics.
                  </p>
                  <p className="text-xs text-purple-700">
                    Perfect for building a comprehensive book quiz platform quickly!
                  </p>
                </div>
                
                {importStats && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-3">Import Statistics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">{importStats.total_available}</div>
                        <div className="text-xs text-blue-700">Books Available</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">{importStats.available_to_import}</div>
                        <div className="text-xs text-green-700">New Books</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-600">{importStats.already_imported}</div>
                        <div className="text-xs text-gray-700">Already Added</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">{Object.keys(importStats.genre_breakdown || {}).length}</div>
                        <div className="text-xs text-purple-700">Genres</div>
                      </div>
                    </div>
                    
                    {importStats.genre_breakdown && Object.keys(importStats.genre_breakdown).length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-blue-900 mb-2">Available by Genre:</h5>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(importStats.genre_breakdown).map(([genre, count]) => (
                            <Badge key={genre} variant="outline" className="text-xs">
                              {genre}: {count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="import_limit">Number of Books to Import</Label>
                    <Select 
                      value={bulkImportData.limit.toString()} 
                      onValueChange={(value) => setBulkImportData({...bulkImportData, limit: parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10 books</SelectItem>
                        <SelectItem value="25">25 books</SelectItem>
                        <SelectItem value="50">50 books</SelectItem>
                        <SelectItem value="100">100 books (Full Collection)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="genre_filter">Filter by Genre (Optional)</Label>
                    <Select 
                      value={bulkImportData.genreFilter} 
                      onValueChange={(value) => setBulkImportData({...bulkImportData, genreFilter: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Genres" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Genres</SelectItem>
                        <SelectItem value="Fiction">Fiction</SelectItem>
                        <SelectItem value="Fantasy">Fantasy</SelectItem>
                        <SelectItem value="Science Fiction">Science Fiction</SelectItem>
                        <SelectItem value="Romance">Romance</SelectItem>
                        <SelectItem value="Adventure">Adventure</SelectItem>
                        <SelectItem value="Gothic Fiction">Gothic Fiction</SelectItem>
                        <SelectItem value="Historical Fiction">Historical Fiction</SelectItem>
                        <SelectItem value="Drama">Drama</SelectItem>
                        <SelectItem value="Epic Poetry">Epic Poetry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleBulkImport} 
                    disabled={bulkImporting}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  >
                    {bulkImporting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Database className="h-4 w-4 mr-2" />
                    )}
                    {bulkImporting ? 'Importing Books...' : `Import ${bulkImportData.limit} Books`}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowBulkImport(false);
                      setImportStats(null);
                      setBulkImportData({ limit: 25, genreFilter: 'all' });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h5 className="text-sm font-semibold text-green-900 mb-1">What's included:</h5>
                  <p className="text-xs text-green-800">
                    Classic literature from authors like Shakespeare, Dickens, Austen, Tolkien, Orwell, 
                    and modern bestsellers. Each book includes title, author, genre, description, publication year, and cover image.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Open Library Import Modal */}
          {showOpenLibraryImport && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-blue-600" />
                  Import from Open Library - Millions of Books Available
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <Book className="h-4 w-4 mr-2" />
                    Open Library Integration
                  </h4>
                  <p className="text-sm text-blue-800 mb-2">
                    Access millions of books from Open Library - the world's largest digital library. 
                    Import by popularity, classic literature, or specific subjects like Fiction, Fantasy, Science Fiction, and more.
                  </p>
                  <p className="text-xs text-blue-700">
                    All books include cover images, descriptions, publication years, ISBN data, and author information!
                  </p>
                </div>
                
                {openLibraryStats && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-3">Open Library Statistics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-blue-600">{openLibraryStats.current_books_in_database}</div>
                        <div className="text-xs text-blue-700">Current Books</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">{openLibraryStats.openlibrary_info.estimated_books_available}</div>
                        <div className="text-xs text-green-700">Available</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-600">{openLibraryStats.openlibrary_info.available_subjects}</div>
                        <div className="text-xs text-purple-700">Subjects</div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="import_type">Import Type</Label>
                    <Select 
                      value={openLibraryData.importType} 
                      onValueChange={(value) => setOpenLibraryData({...openLibraryData, importType: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="popular">Popular Books</SelectItem>
                        <SelectItem value="classics">Classic Literature</SelectItem>
                        <SelectItem value="subject">Books by Subject</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="limit">Number of Books</Label>
                    <Select 
                      value={openLibraryData.limit.toString()} 
                      onValueChange={(value) => setOpenLibraryData({...openLibraryData, limit: parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25 books</SelectItem>
                        <SelectItem value="50">50 books</SelectItem>
                        <SelectItem value="100">100 books</SelectItem>
                        <SelectItem value="200">200 books</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {openLibraryData.importType === 'subject' && (
                  <div>
                    <Label htmlFor="subject">Select Subject/Genre</Label>
                    <Select 
                      value={openLibraryData.subject} 
                      onValueChange={(value) => setOpenLibraryData({...openLibraryData, subject: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSubjects.map(subject => (
                          <SelectItem key={subject} value={subject}>
                            {subject.charAt(0).toUpperCase() + subject.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Button 
                    onClick={handleOpenLibraryImport} 
                    disabled={openLibraryImporting}
                    className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                  >
                    {openLibraryImporting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Globe className="h-4 w-4 mr-2" />
                    )}
                    {openLibraryImporting ? 'Importing Books...' : `Import ${openLibraryData.limit} Books`}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowOpenLibraryImport(false);
                      setOpenLibraryStats(null);
                      setOpenLibraryData({ importType: 'popular', subject: 'fiction', limit: 50 });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-blue-900 mb-1">📚 What you get:</h5>
                    <p className="text-xs text-blue-800">
                      • Complete book metadata (title, author, genre, description, publication year)<br />
                      • High-quality cover images from Open Library<br />
                      • ISBN numbers and page counts when available<br />
                      • Books from verified publishers and authors
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-purple-900 mb-1">🎯 Import Types:</h5>
                    <p className="text-xs text-purple-800">
                      <strong>Popular:</strong> Trending and highly-rated books<br />
                      <strong>Classics:</strong> Timeless literature from renowned authors<br />
                      <strong>By Subject:</strong> Focus on specific genres like Fiction, Fantasy, Mystery, etc.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add/Edit Form */}
          {showAddForm && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="author">Author *</Label>
                      <Input
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="genre">Genre *</Label>
                      <Input
                        id="genre"
                        name="genre"
                        value={formData.genre}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="published_year">Published Year *</Label>
                      <Input
                        id="published_year"
                        name="published_year"
                        type="number"
                        value={formData.published_year}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="cover_url">Cover URL</Label>
                    <Input
                      id="cover_url"
                      name="cover_url"
                      value={formData.cover_url}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit">
                      {editingBook ? 'Update Book' : 'Create Book'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={resetForm}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Books Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {books.map((book) => (
                  <Card key={book.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all">
                    <CardHeader className="pb-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 pr-4">
                          <Badge className="mb-2 bg-blue-100 text-blue-800 border-blue-200">
                            {book.genre}
                          </Badge>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                            {book.title}
                          </h3>
                          <p className="text-gray-600 font-medium">by {book.author}</p>
                        </div>
                        {book.cover_url && (
                          <img
                            src={book.cover_url}
                            alt={book.title}
                            className="w-16 h-20 object-cover rounded"
                            loading="lazy"
                          />
                        )}
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <p className="text-gray-700 text-sm line-clamp-3">{book.description}</p>

                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Published: {book.published_year}</span>
                        <span>{book.total_quizzes || 0} quizzes</span>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => openAIQuizModal(book)}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          AI Quiz
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(book)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(book)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <BookPagination
                currentPage={currentPage}
                totalPages={Math.ceil(totalBooks / ADMIN_PAGE_SIZE)}
                onPageChange={setCurrentPage}
                totalItems={totalBooks}
                pageSize={ADMIN_PAGE_SIZE}
              />
            </>
          )}
          {books.length === 0 && !loading && (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm ? 'No books found' : 'No books yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm ? 'Try adjusting your search terms' : 'Start by adding your first book'}
              </p>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Book
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminBooks;
